import { useEffect, useState } from "react";
import { settings } from "app";

import {
  List,
  TextField,
  SimpleList,
  Datagrid,
  useNotify,
  useDataProvider,
  Title,
  FunctionField,
  ReferenceField,
  useStore,
} from "react-admin";
import { Box, Typography, Paper, useMediaQuery, Divider } from "@mui/material";

import { CampaignSubTypeLabels, CampaignSubTypes, MOBILE_BREAKPOINT, RankingResultEnroll } from "utils/constants";
import { candidateRequests } from "api/candidateApi";
import { useNotifyError } from "utils/useNotifyError";
import CandidateStudentsFilters from "./Filters";
import RankingSummary from "./RankingSummary";
import { EmptyList } from "server/components";
import CustomFilter from "features/CustomFilter";
import { administrationRequests } from "api/administrationApi";

import { EnrollingButton } from "./EnrollingButton";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import { MobileListRow } from "features/MobileListRow";

const ListAcceptedStudents = () => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const dataProvider = useDataProvider();

  const [campaignChoices, setCmapaignChoices] = useState([]);
  const [specialiyChoices, setSpecialityChoices] = useState([]);

  const STORE_KEY = "studentsEnrollment.filters";

  // filters drives the <List>, RankingSummary, citizenship fetch and the gate (persisted via useStore);
  // campaignIds/specialityId are the draft selections bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(STORE_KEY, null);
  const [campaignIds, setCampaignIds] = useState(filters?.campaignId ?? null);
  const [specialityId, setSpecialityId] = useState(filters?.specialityId ?? null);

  // Campaign choices cover regular, paid and hospital contract campaigns; the sub type label is
  // appended to the option name so campaigns of the same year stay distinguishable. The bare campaign
  // name is kept in searchName so the appended label does not take part in the autocomplete search.
  useEffect(() => {
    const withSubTypeLabel = (items, subType) =>
      (items ?? []).map(({ nameBg, name, ...rest }) => {
        const campaignName = nameBg || name;
        return {
          ...rest,
          name: `${campaignName} (${CampaignSubTypeLabels[subType]})`,
          searchName: campaignName,
        };
      });

    Promise.all(
      [CampaignSubTypes.Regular, CampaignSubTypes.Paid, CampaignSubTypes.HospitalContract].map(subType =>
        dataProvider
          .customRequest(candidateRequests.getPreliminaryCampaignsSelect(subType))
          .then(res => withSubTypeLabel(res.data, subType)),
      ),
    )
      .then(results => {
        setCmapaignChoices(results.flat());
      })
      .catch(error => {
        notifyError(error);
      });
  }, [dataProvider, notifyError]);

  // Speciality choices follow the draft campaign selection.
  useEffect(() => {
    if (!campaignIds) {
      setSpecialityId(null);
      setSpecialityChoices([]);
      return;
    }
    dataProvider
      .customRequest(candidateRequests.getCampaignSpecialitiesSelect(campaignIds))
      .then(res => {
        setSpecialityChoices(res.data);
      })
      .catch(error => {
        notifyError(error);
      });
  }, [campaignIds, dataProvider, notifyError]);

  const searchParams = () => {
    if (!(campaignIds && specialityId)) {
      notify("Моля въведете специалност и кампания!", { type: "error" });
      return;
    }
    dataProvider
      .customRequest(candidateRequests.getLastApprovedCampaignRanking({ campaignId: campaignIds, specialityId }))
      .then(res => {
        setFilters({
          campaignId: campaignIds,
          specialityId,
          rankingId: res?.data?.campaignRankingId ?? null,
        });
      })
      .catch(error => {
        notifyError(error);
      });
  };

  return (
    <>
      {!filters && <Title title="Записване на приети студенти " />}
      <CustomFilter
        title="Списък приети студенти"
        filters={[
          {
            label: "Кампания",
            value: campaignIds,
            setValue: v => {
              setCampaignIds(v);
              setSpecialityId(null);
            },
            type: "autocomplete",
            multiple: false,
            choices: campaignChoices,
            // Match the campaign name only - the sub type label in brackets is display-only.
            filterOptions: (options, { inputValue }) => {
              const query = String(inputValue).trim().toLowerCase();
              if (!query) return options;
              return options.filter(option => option.searchName?.toLowerCase().includes(query));
            },
          },
          {
            label: "Специалност",
            value: specialityId,
            setValue: setSpecialityId,
            type: "select",
            multiple: false,
            choices: specialiyChoices,
          },
        ]}
        cardWidth={isMobile ? "350px" : "700px"}
        buttonLabel="Провери"
        buttonOnClick={searchParams}
      />
      {!filters && (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color={"secondary"}
            >
              Въведи данни за търсене на студенти.
            </Typography>
          </Box>
        </Paper>
      )}
      {filters && (
        <>
          <Divider
            variant="middle"
            orientation="horizontal"
            sx={{ marginY: 2 }}
          />
          {filters.rankingId && (
            <RankingSummary
              specialityId={filters.specialityId}
              campaignRankingId={filters.rankingId}
            />
          )}
          <List
            filters={CandidateStudentsFilters}
            title="Списък с приети студенти"
            sx={settings.style.blockStyle}
            resource={`candidate/campaigns/ranking/last/approved`}
            filter={{ campaignId: filters.campaignId, specialityId: filters.specialityId }}
            empty={<EmptyList />}
            exporter={false}
          >
            {isMobile ? (
              <SimpleList
                empty={<EmptyList />}
                linkType={false}
                sx={settings.style.mobileList}
                primaryText={record => (
                  <div>
                    <MobileListRow fields={[{ label: "Имена", value: record.names }]} />
                    <MobileListRow fields={[{ label: "Вх. номер", value: record.admissionNumber }]} />
                  </div>
                )}
                secondaryText={record => (
                  <div>
                    <MobileListRow fields={[{ label: "ЕГН", value: record.personId }]} />
                    <MobileListRow fields={[{ label: "Телефон", value: record.contactPhone }]} />
                    <MobileListRow fields={[{ label: "Имейл", value: record.email }]} />
                    <MobileListRow
                      fields={[{ label: "Класиране в група", value: RankingResultEnroll[record?.group] }]}
                    />
                    <MobileListRow
                      fields={[
                        {
                          label: "Държава",
                          value: (
                            <ReferenceField
                              source="residenceCountryId"
                              reference={administrationRequests.getCountriesSelect()}
                            >
                              <TextField source="nameBg" />
                            </ReferenceField>
                          ),
                        },
                      ]}
                    />
                    <MobileListRow fields={[{ label: "Бал", value: record?.scoreFormatted }]} />
                    <SecuredComponent permission={permissions.canChangeAcademicRequestStatus}>
                      <EnrollingButton
                        label="Записване"
                        specialityId={filters.specialityId}
                        candidacyId={record.candidacyId}
                      />
                    </SecuredComponent>
                  </div>
                )}
              />
            ) : (
              <Datagrid
                bulkActionButtons={false}
                rowClick={false}
                sx={settings.style.firstExpandIconDatagrid}
              >
                <TextField
                  source="admissionNumber"
                  label="Вх. номер"
                  sortable={false}
                />
                <TextField
                  label="Имена"
                  source="names"
                  sortable={false}
                />
                <TextField
                  label="ЕГН"
                  source="personId"
                  sortable={false}
                />
                <TextField
                  label="Телефон"
                  source="contactPhone"
                  sortable={false}
                />
                <TextField
                  label="Имейл"
                  source="email"
                  sortable={false}
                />

                <FunctionField
                  label="Класиране в група:"
                  render={record => <Typography>{RankingResultEnroll[record?.group]}</Typography>}
                />

                <ReferenceField
                  source="residenceCountryId"
                  reference={administrationRequests.getCountriesSelect()}
                  label="Държава"
                >
                  <TextField source="nameBg" />
                </ReferenceField>
                <TextField
                  label="Бал"
                  source="scoreFormatted"
                  sortable={false}
                />
                <SecuredComponent permission={permissions.canChangeAcademicRequestStatus}>
                  <FunctionField
                    label=""
                    render={record => (
                      <EnrollingButton
                        specialityId={filters.specialityId}
                        candidacyId={record.candidacyId}
                      />
                    )}
                  />
                </SecuredComponent>
              </Datagrid>
            )}
          </List>
        </>
      )}
    </>
  );
};
export default ListAcceptedStudents;
